import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Loader2, Bot, Sparkles, Bug, RefreshCw, MessageSquarePlus } from 'lucide-react'
import { ChatMessage as ChatMessageType, ChatSession, BudgetRecommendation } from '../../types/ai-budget'
import ChatMessage from './ChatMessage'
import BudgetSaveCard from './BudgetSaveCard'
import { geminiService } from '../../services/geminiService'
import { checkDatabaseHealth } from '../../utils/databaseHealthCheck'

interface AIBudgetChatProps {
  session: ChatSession | null
  messages: ChatMessageType[]
  isLoading: boolean
  error: string | null
  onSendMessage: (message: string) => Promise<void>
  onClearError: () => void
  onCreateNewSession: () => Promise<ChatSession>
  latestBudgetRecommendation: BudgetRecommendation | null
  budgetMonthlyIncome: number
  onSaveBudget: (budgetName: string, description?: string) => Promise<void>
  className?: string
}

const AIBudgetChat: React.FC<AIBudgetChatProps> = ({
  session,
  messages,
  isLoading,
  error,
  onSendMessage,
  onClearError,
  onCreateNewSession,
  latestBudgetRecommendation,
  budgetMonthlyIncome,
  onSaveBudget,
  className = ''
}) => {
  const [inputMessage, setInputMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [showClearConfirmation, setShowClearConfirmation] = useState(false)
  const [isCreatingNewSession, setIsCreatingNewSession] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!inputMessage.trim() || isLoading) return

    const message = inputMessage.trim()
    setInputMessage('')
    setIsTyping(true)

    try {
      await onSendMessage(message)
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setIsTyping(false)
    }
  }

  // Auto-resize textarea as user types
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = '48px' // Reset height
      const scrollHeight = inputRef.current.scrollHeight
      inputRef.current.style.height = Math.min(scrollHeight, 120) + 'px'
    }
  }, [inputMessage])

  const [serviceStatus, setServiceStatus] = useState<'checking' | 'available' | 'unavailable'>('checking')

  // Check service availability on mount
  useEffect(() => {
    const checkService = async () => {
      try {
        const isAvailable = await geminiService.checkAvailability()
        setServiceStatus(isAvailable ? 'available' : 'unavailable')
      } catch (error) {
        console.error('Failed to check service availability:', error)
        setServiceStatus('unavailable')
      }
    }
    checkService()
  }, [])

  const handleDebugService = async () => {
    try {
      console.log('=== AI BUDGET SYSTEM DEBUG ===')
      
      // Check Gemini Service
      console.log('\nGemini AI Service:')
      console.log('   API Key configured:', !!import.meta.env.VITE_GEMINI_API_KEY)
      console.log('   Service available:', geminiService.isAvailable())
      
      const isAvailable = await geminiService.checkAvailability()
      console.log('   Service will be available:', isAvailable)
      
      if (isAvailable) {
        const modelInfo = geminiService.getCurrentModelInfo()
        
        console.log('   Current Model:', modelInfo.model)
        console.log('   Cost Info:', modelInfo.costInfo)
      }
      
      // Check Database Health
      console.log('\nDatabase Health Check:')
      const dbHealth = await checkDatabaseHealth()
      
      if (dbHealth.isHealthy) {
        console.log('   Database is healthy!')
      } else {
        console.log('   Database issues found:')
        if (dbHealth.missingTables.length > 0) {
          console.log('   Missing tables:', dbHealth.missingTables.join(', '))
        }
        if (dbHealth.errors.length > 0) {
          console.log('   Errors:', dbHealth.errors.join(', '))
        }
        
        console.log('\nRecommendations:')
        dbHealth.recommendations.forEach((rec, index) => {
          console.log(`   ${index + 1}. ${rec}`)
        })
      }
      
      // Check Current Session
      console.log('\nCurrent Session:')
      console.log('   Session ID:', session?.id || 'None')
      console.log('   Messages count:', messages.length)
      console.log('   Session status:', session?.status || 'N/A')
      
      console.log('\nAvailable Actions:')
      console.log('   Database Issues: Run ai_budget_migration.sql in Supabase')
      console.log('   Refresh Chat: Use the refresh button in the header')
      console.log('   Cost Control: System automatically uses ONLY Gemini 1.5 Flash (cheapest)')
      
      // Show model selection result
      if (isAvailable) {
        const modelInfo = geminiService.getCurrentModelInfo()
        if (modelInfo.model.includes('1.5-flash')) {
          console.log('\nCOST OPTIMIZATION ACTIVE:')
          console.log('   Using cheapest Gemini 1.5 Flash model - maximum cost savings!')
        } else {
          console.log('\nWARNING: Not using Flash model - this may be expensive!')
          console.log('   Current model:', modelInfo.model)
          console.log('   To fix: Run geminiService.forceReInitialize() to switch to Flash-only mode')
        }
      }
      
      // Manual fix for expensive model usage
      if (isAvailable) {
        const modelInfo = geminiService.getCurrentModelInfo()
        if (!modelInfo.model.includes('1.5-flash')) {
          console.log('\nAPPLY COST CONTROL NOW:')
          console.log('   Copy and paste: await geminiService.forceReInitialize()')
          console.log('   This will switch to ONLY the cheapest Gemini 1.5 Flash models')
        }
      }
      
    } catch (error) {
      console.error('Debug check failed:', error)
    }
  }

  const handleClearChat = () => {
    console.log('Clear chat button clicked!')
    console.log('Current state:', {
      messagesLength: messages.length,
      sessionId: session?.id,
      isLoading,
      isCreatingNewSession
    })
    
    if (messages.length > 0) {
      console.log('Messages exist, showing confirmation dialog')
      setShowClearConfirmation(true)
    } else {
      console.log('No messages, creating new session directly')
      handleCreateNewSession()
    }
  }

  const handleCreateNewSession = async () => {
    if (isCreatingNewSession || isLoading) return
    
    console.log('Starting new chat session from confirmation dialog...')
    console.log('User clicked "Yes, Start Fresh" button')
    
    try {
      setIsCreatingNewSession(true)
      setShowClearConfirmation(false)
      
      console.log('Calling onCreateNewSession...')
      const newSession = await onCreateNewSession()
      console.log('New session created:', newSession)
      
      // Clear any local state
      setInputMessage('')
      
      // Force a re-render by clearing any cached welcome message
      setTimeout(() => {
        console.log('New chat session ready - refresh complete!')
      }, 100)
      
    } catch (error) {
      console.error('Failed to create new session:', error)
      
      // If it's a database table issue, provide helpful guidance
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      if (errorMessage.includes('ai_chat_sessions') && errorMessage.includes('does not exist')) {
        alert('Database setup required!\n\nThe AI chat database tables are missing. Please:\n1. Check your database setup\n2. Run the database migration scripts\n3. Ensure Supabase is properly configured')
      } else {
        alert(`Failed to create new chat session: ${errorMessage}\n\nYou can still use the chat, but conversation history may not be saved.`)
      }
      
      // Even if database fails, try to clear the local chat state
      try {
        console.log('Clearing local chat state as fallback...')
        // Force clear local state even without database
        setInputMessage('')
        
        // Trigger a local "refresh" by creating a mock session
        const _mockSession = {
          id: `local-${Date.now()}`,
          user_id: 'local',
          title: 'Local Chat Session',
          status: 'active' as const,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        console.log('Mock session created:', _mockSession.id)
        
        // This won't actually create a session, but will trigger UI updates
        console.log('Using local fallback session for UI refresh')
        
      } catch (fallbackError) {
        console.error('Even fallback failed:', fallbackError)
      }
    } finally {
      setIsCreatingNewSession(false)
    }
  }

  const handleCancelClear = () => {
    console.log('User cancelled chat refresh')
    setShowClearConfirmation(false)
  }

  const getWelcomeMessage = () => {
    if (messages.length === 0) {
      const isNewSession = session && new Date(session.created_at).getTime() > Date.now() - 10000 // Within last 10 seconds
      
      return {
        id: `welcome-${session?.id || 'initial'}`, // Unique ID per session
        session_id: session?.id || '',
        role: 'assistant' as const,
        content: `${isNewSession ? '[NEW] ' : ''}Hey! I'm **Kubera**, your budget assistant. I'm here to help you build a smart budget through a quick conversation.

**Beta Notice**: I'm in beta - my advice is a great starting point, but consult professionals for major decisions.

${isNewSession ? '*Starting fresh!*\n\n' : ''}Let's chat about your finances. First up - what's your monthly income? (Don't worry, this is completely private.)`,
        metadata: { isWelcome: true, sessionId: session?.id },
        created_at: new Date().toISOString()
      }
    }
    return null
  }

  const welcomeMessage = getWelcomeMessage()
  const allMessages = welcomeMessage ? [welcomeMessage, ...messages] : messages

  return (
    <div className={`flex flex-col h-full bg-background-900 rounded-xl ${className}`}>
      {/* Chat Header */}
      <div className="flex items-center justify-between p-4 border-b border-background-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-background-100">
                Kubera
              </h3>
              <span className="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded-full border border-orange-500/30">
                BETA
              </span>
            </div>
            <p className="text-sm text-background-400">
              {session ? session.title : 'Creating your personalized budget'}
            </p>
          </div>
        </div>
        
        {/* Status Indicator */}
        <div className="flex items-center space-x-2">
          {/* Clear Chat Button */}
          <button
            onClick={handleClearChat}
            disabled={isCreatingNewSession || isLoading}
            className="p-2 hover:bg-background-700 rounded-lg transition-colors text-background-400 hover:text-accent-400 disabled:opacity-50 disabled:cursor-not-allowed"
            title={messages.length > 0 ? "Clear chat and start new conversation" : "Start new conversation"}
          >
            {isCreatingNewSession ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : messages.length > 0 ? (
              <RefreshCw className="w-4 h-4" />
            ) : (
              <MessageSquarePlus className="w-4 h-4" />
            )}
          </button>

          {/* Debug Button */}
          <button
            onClick={handleDebugService}
            className="p-2 hover:bg-background-700 rounded-lg transition-colors text-background-400 hover:text-accent-400"
            title="Debug: Check service status (check console)"
          >
            <Bug className="w-4 h-4" />
          </button>
          
          {serviceStatus === 'checking' && (
            <div className="flex items-center space-x-1 text-yellow-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Initializing...</span>
            </div>
          )}
          {serviceStatus === 'unavailable' && (
            <div className="flex items-center space-x-1 text-red-400">
              <div className="w-2 h-2 bg-red-400 rounded-full"></div>
              <span className="text-sm">Unavailable</span>
            </div>
          )}
          {serviceStatus === 'available' && !isLoading && (
            <div className="flex items-center space-x-1 text-green-400">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">Ready</span>
            </div>
          )}
          {serviceStatus === 'available' && isLoading && (
            <div className="flex items-center space-x-1 text-accent-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div 
        key={`chat-${session?.id || 'no-session'}`} // Force re-render when session changes
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/20 rounded-lg p-3"
          >
            <div className="flex items-center justify-between">
              <p className="text-red-400 text-sm">{error}</p>
              <button
                onClick={onClearError}
                className="text-red-400 hover:text-red-300 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          </motion.div>
        )}

        {serviceStatus === 'unavailable' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4"
          >
            <div className="flex items-start space-x-3">
              <Bot className="w-6 h-6 text-yellow-500 mt-0.5" />
              <div>
                <h4 className="text-yellow-400 font-medium mb-2">AI Service Unavailable</h4>
                <p className="text-yellow-300 text-sm mb-3">
                  The AI budget assistant is currently unavailable. This might be due to:
                </p>
                <ul className="text-yellow-300 text-sm space-y-1 ml-4">
                  <li>• Invalid or missing API key</li>
                  <li>• Network connectivity issues</li>
                  <li>• Gemini API service problems</li>
                </ul>
                <div className="mt-3 text-xs text-yellow-400">
                  Click the debug button above and check the console for details
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Clear Chat Confirmation - PROMINENT OVERLAY */}
        {showClearConfirmation && (
          <>
            {/* Overlay Background */}
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-background-900 border-2 border-accent-500 rounded-xl p-8 shadow-2xl max-w-md w-full"
              >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-accent-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-6 h-6 text-accent-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-white font-bold mb-2 text-xl">CLEAR CHAT & START FRESH?</h4>
                <p className="text-background-300 text-sm mb-1">
                  You have <strong className="text-accent-400">{messages.length} messages</strong> in this conversation.
                </p>
                <p className="text-background-300 text-sm mb-4">
                  Starting fresh will save this conversation and begin with a clean slate.
                </p>
                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCreateNewSession}
                    disabled={isCreatingNewSession}
                    className="px-8 py-3 bg-accent-500 hover:bg-accent-600 text-white rounded-lg text-base font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl border-2 border-accent-400 hover:shadow-accent-500/25"
                  >
                    {isCreatingNewSession ? (
                      <span className="flex items-center space-x-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Creating New Chat...</span>
                      </span>
                    ) : (
                      <span className="flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4" />
                        <span>Yes, Start Fresh</span>
                      </span>
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCancelClear}
                    disabled={isCreatingNewSession}
                    className="px-4 py-2 bg-background-700 hover:bg-background-600 text-background-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 border border-background-600"
                  >
                    Keep Current Chat
                  </motion.button>
                </div>
              </div>
            </div>
              </motion.div>
            </div>
          </>
        )}

        <AnimatePresence mode="popLayout">
          {allMessages.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message}
              isLatest={index === allMessages.length - 1}
            />
          ))}
        </AnimatePresence>

        {/* Typing Indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex items-center space-x-3"
          >
            <div className="w-8 h-8 bg-accent-500/20 rounded-full flex items-center justify-center">
              <Bot className="w-4 h-4 text-accent-400" />
            </div>
            <div className="bg-background-800 rounded-lg px-4 py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-background-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-background-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-background-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Budget Save Card */}
        {latestBudgetRecommendation && budgetMonthlyIncome > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <BudgetSaveCard
              budgetRecommendation={latestBudgetRecommendation}
              monthlyIncome={budgetMonthlyIncome}
              onSaveBudget={onSaveBudget}
              className="my-4"
            />
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <div className="p-4 border-t border-background-700">
        <form onSubmit={handleSubmit} className="flex space-x-3">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit(e)
                }
              }}
              placeholder={serviceStatus === 'unavailable' 
                ? "AI service is unavailable - check your configuration" 
                : serviceStatus === 'checking' 
                  ? "Initializing AI service..."
                  : "Tell me about your income, expenses, or financial goals..."
              }
              disabled={isLoading || serviceStatus !== 'available'}
              rows={1}
              className="w-full px-4 py-3 bg-background-800 border border-background-700 rounded-lg text-background-100 placeholder-background-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed pr-12 resize-none overflow-y-auto"
              style={{ 
                minHeight: '48px',
                maxHeight: '120px'
              }}
            />
            {inputMessage.trim() && (
              <div className="absolute right-3 top-3 text-background-400 text-sm">
                {inputMessage.length}/500
              </div>
            )}
          </div>
          
          <motion.button
            type="submit"
            disabled={!inputMessage.trim() || isLoading || serviceStatus !== 'available'}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-3 bg-accent-500 hover:bg-accent-600 disabled:bg-background-700 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center min-w-[50px]"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <Send className="w-5 h-5 text-white" />
            )}
          </motion.button>
        </form>

        {/* Quick Actions */}
        <div className="mt-3 flex flex-wrap gap-2">
          {messages.length === 0 && (
            <>
              <button
                onClick={() => setInputMessage("$5000 per month")}
                className="px-3 py-1 bg-background-800 hover:bg-background-700 text-background-300 text-sm rounded-full transition-colors"
              >
                $5k/month
              </button>
              <button
                onClick={() => setInputMessage("$3000 per month")}
                className="px-3 py-1 bg-background-800 hover:bg-background-700 text-background-300 text-sm rounded-full transition-colors"
              >
                $3k/month
              </button>
              <button
                onClick={() => setInputMessage("$7500 per month")}
                className="px-3 py-1 bg-background-800 hover:bg-background-700 text-background-300 text-sm rounded-full transition-colors"
              >
                $7.5k/month
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AIBudgetChat
